import { useState } from 'react';
import { Activity, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const FRONTEND_BASE = (typeof window !== 'undefined') ? window.location.origin : '';

/**
 * V1 — Cross-coach recruitment hook for Performance Coaches.
 * Renders when a Performance Coach owns an athlete but the profile lacks
 * Soccer Coach (private_soccer) and/or Club Coach (team) evaluations.
 *
 * Pure presentational helper — receives `sources` shape and athleteName.
 */
const ProfileCompletenessBanner = ({ sources, athleteName }) => {
  const [copied, setCopied] = useState(false);

  const hasSoccer = sources?.has_private_soccer;
  const hasTeam = sources?.has_team;
  const hasPerformance = sources?.has_private_performance;

  // Banner triggers when:
  //   - Performance is present (so the coach actually completed their part), AND
  //   - Private Soccer Coach is missing.
  // Club Coach is NOT a self-serve role (they're added by the Club Admin),
  // so we never ask the family to "invite" one — only Soccer Coach is
  // surfaced as an actionable gap. The completeness % still accounts for
  // every source actually present.
  const presentCount = [hasSoccer, hasTeam, hasPerformance].filter(Boolean).length;
  if (!hasPerformance || hasSoccer) return null;

  // Completeness % mirrors the backend formula (40 / 75 / 100)
  const completeness = { 1: 40, 2: 75 }[presentCount] || 0;
  const message = `Hi! ${athleteName} has a great physical profile in place. To unlock the full athlete development picture (technical and tactical), connect a Private Soccer Coach via Meybra: ${FRONTEND_BASE}/get-started`;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Referral message copied — paste in WhatsApp/Email');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <div className="rounded-lg border border-orange-400/30 bg-gradient-to-r from-orange-400/10 to-orange-400/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="profile-completeness-banner">
      <div className="flex items-center gap-3 flex-1">
        <div className="w-10 h-10 rounded-md bg-orange-400/15 flex items-center justify-center shrink-0">
          <Activity className="text-orange-300" size={18} />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-semibold">Profile is {completeness}% complete</p>
            <span className="text-[10px] uppercase tracking-widest text-orange-300 bg-orange-400/15 border border-orange-400/30 rounded-full px-2 py-0.5">
              Physical ✓
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Invite a <span className="text-foreground font-medium">Private Soccer Coach</span> to unlock technical &amp; tactical evaluations.
          </p>
        </div>
      </div>
      <Button
        onClick={copyMessage}
        variant="outline"
        size="sm"
        className="border-orange-400/40 text-orange-200 hover:bg-orange-400/15 shrink-0"
        data-testid="completeness-share-btn"
      >
        {copied ? <Check size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
        {copied ? 'Copied' : 'Share with Family'}
      </Button>
    </div>
  );
};

export default ProfileCompletenessBanner;
